import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import PublicLayout from './pages/public/Layout';
import { Home, ClipboardCheck, GraduationCap, Bell, Calendar, Ribbon, Wallet, School, BookOpen, Clock, User, MessageSquare, Users, Settings, ChevronDown, ChevronRight, MoreHorizontal, Compass, Megaphone, BarChart2 } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { COLORS, getAvatarColor, getInitials } from './constants/theme';
import client, { resolveUrl } from './api/client';
import { useSchoolProfile, logoCacheBust } from './hooks/useSchoolProfile';
import { SchoolBrandProvider, useSchoolBrand } from './context/SchoolBrandContext';
import TermsModal, { CURRENT_TERMS_VERSION } from './components/TermsModal';
import AdminNotificationBell from './components/AdminNotificationBell';
import { useTranslation } from 'react-i18next';

/* ── Public marketing pages ── */
const PublicHome        = lazy(() => import('./pages/public/Home'));
const PublicFeatures    = lazy(() => import('./pages/public/Features'));
const PublicPricing     = lazy(() => import('./pages/public/Pricing'));
const PublicAbout       = lazy(() => import('./pages/public/About'));
const PublicContact     = lazy(() => import('./pages/public/Contact'));
const PublicRequestDemo = lazy(() => import('./pages/public/RequestDemo'));
const PublicPrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));

/* ── Super Admin Portal ── */
const SuperAdminPortal = lazy(() => import('./pages/superadmin/SuperAdminPortal'));

/* ── Impersonation Banner — shown in AdminLayout when SA is impersonating ── */
function ImpersonationBanner() {
  const data = (() => { try { return JSON.parse(localStorage.getItem('impersonating') || 'null'); } catch { return null; } })();
  if (!data) return null;
  const returnToSA = () => {
    localStorage.removeItem('impersonating');
    localStorage.removeItem('token');
    window.location.href = '/sa';
  };
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 13, fontWeight: 600 }}>
        <span style={{ fontSize: 16 }}>🔑</span>
        You are currently impersonating <strong style={{ marginLeft: 4 }}>{data.school_name}</strong> Admin.
      </div>
      <button onClick={returnToSA} style={{
        padding: '6px 16px', background: 'rgba(255,255,255,0.2)',
        border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8,
        color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}>
        ← Return to Super Admin
      </button>
    </div>
  );
}

/* ── Lazy-loaded Pages ── */
const LoginGateway   = lazy(() => import('./pages/LoginGateway'));
const Login          = lazy(() => import('./pages/Login'));
const ParentLogin    = lazy(() => import('./pages/ParentLogin'));
const StudentLogin   = lazy(() => import('./pages/StudentLogin'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ChangePIN      = lazy(() => import('./pages/ChangePIN'));
const PayReturn      = lazy(() => import('./pages/PayReturn'));

/* Teacher */
const TeacherHome          = lazy(() => import('./pages/teacher/Home'));
const AttendanceSelectClass= lazy(() => import('./pages/teacher/AttendanceSelectClass'));
const AttendanceMarking    = lazy(() => import('./pages/teacher/AttendanceMarking'));
const GradesSelectClass    = lazy(() => import('./pages/teacher/GradesSelectClass'));
const GradesTable          = lazy(() => import('./pages/teacher/GradesTable'));
const TeacherAlerts        = lazy(() => import('./pages/teacher/Alerts'));
const TeacherProfile       = lazy(() => import('./pages/teacher/Profile'));
const ClassNotes           = lazy(() => import('./pages/teacher/ClassNotes'));
const ViewStudents         = lazy(() => import('./pages/teacher/ViewStudents'));
const StudentDetail        = lazy(() => import('./pages/teacher/StudentDetail'));
const ClassReport          = lazy(() => import('./pages/teacher/ClassReport'));
const MyTimetable          = lazy(() => import('./pages/teacher/MyTimetable'));
const TeacherMessages      = lazy(() => import('./pages/teacher/Messages'));
const TeacherHomework      = lazy(() => import('./pages/teacher/Homework'));
const CreateHomework       = lazy(() => import('./pages/teacher/CreateHomework'));
const TeacherSubmissions   = lazy(() => import('./pages/teacher/Submissions'));
const ClassDashboard          = lazy(() => import('./pages/teacher/ClassDashboard'));
const NotificationCenter      = lazy(() => import('./pages/teacher/NotificationCenter'));
const TeacherPayslip          = lazy(() => import('./pages/teacher/Payslip'));

/* Admin */
const AdminDashboard   = lazy(() => import('./pages/admin/Dashboard'));
const AdminStudents    = lazy(() => import('./pages/admin/Students'));
const AdminTeachers    = lazy(() => import('./pages/admin/Teachers'));
const AdminClasses     = lazy(() => import('./pages/admin/Classes'));
const AdminClassDetail = lazy(() => import('./pages/admin/ClassDetail'));
const AdminSubjects    = lazy(() => import('./pages/admin/Subjects'));
const AdminParents      = lazy(() => import('./pages/admin/Parents'));
const AdminParentDetail = lazy(() => import('./pages/admin/ParentDetail'));
const AdminParentSlip   = lazy(() => import('./pages/admin/ParentSlip'));
const AdminFees        = lazy(() => import('./pages/admin/Fees'));
const AdminFeeTypes    = lazy(() => import('./pages/admin/FeeTypes'));
const AdminReports     = lazy(() => import('./pages/admin/Reports'));
const TimetableBuilder = lazy(() => import('./pages/admin/TimetableBuilder'));
const GatewaySettings  = lazy(() => import('./pages/admin/GatewaySettings'));
const SettingsSchoolProfile = lazy(() => import('./pages/admin/settings/SchoolProfile'));
const SettingsAcademicYears = lazy(() => import('./pages/admin/settings/AcademicYears'));
const SettingsTerms         = lazy(() => import('./pages/admin/settings/Terms'));
const SettingsUsersRoles    = lazy(() => import('./pages/admin/settings/UsersRoles'));
const SettingsPayroll       = lazy(() => import('./pages/admin/settings/PayrollSettings'));
const AdminAnnouncements    = lazy(() => import('./pages/admin/Announcements'));
const AdminGuideCenter      = lazy(() => import('./pages/admin/GuideCenterPage'));
const AdminPayroll          = lazy(() => import('./pages/admin/Payroll'));

/* Parent */
const ParentHome       = lazy(() => import('./pages/parent/Home'));
const ParentAttendance = lazy(() => import('./pages/parent/Attendance'));
const ParentGrades     = lazy(() => import('./pages/parent/Grades'));
const ParentFees       = lazy(() => import('./pages/parent/Fees'));
const ParentHomework   = lazy(() => import('./pages/parent/Homework'));
const ParentMessages   = lazy(() => import('./pages/parent/Messages'));
const ParentProfile    = lazy(() => import('./pages/parent/Profile'));
const ParentTimetable  = lazy(() => import('./pages/parent/Timetable'));

/* Student */
const StudentHome          = lazy(() => import('./pages/student/Home'));
const StudentHomework      = lazy(() => import('./pages/student/Homework'));
const StudentGrades        = lazy(() => import('./pages/student/Grades'));
const StudentAttendance    = lazy(() => import('./pages/student/Attendance'));
const StudentTimetable     = lazy(() => import('./pages/student/Timetable'));
const StudentAnnouncements = lazy(() => import('./pages/student/Announcements'));
const StudentProfile       = lazy(() => import('./pages/student/Profile'));

/* Admin — Student Slip */
const AdminStudentSlip     = lazy(() => import('./pages/admin/StudentSlip'));

/* ── Page loading fallback ── */
function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', minHeight: 200, color: '#9CA3AF', fontSize: 14,
    }}>
      Loading…
    </div>
  );
}

/* ─── Settings Nav Item (collapsible) ── */
const SETTINGS_SUB_ITEMS = [
  { to: '/admin/settings/school-profile', label: 'School Profile' },
  { to: '/admin/settings/academic-years', label: 'Academic Years' },
  { to: '/admin/settings/terms',          label: 'Terms & Grading' },
  { to: '/admin/settings/users-roles',    label: 'Users & Roles' },
  { to: '/admin/fees/gateway',            label: 'Payment Gateway' },
];

function SettingsNavItem({ location, navigate }) {
  const isSettingsActive = location.pathname.startsWith('/admin/settings') || location.pathname === '/admin/fees/gateway';
  const [open, setOpen] = React.useState(isSettingsActive);

  // Auto-open when navigating to a settings sub-page
  React.useEffect(() => {
    if (isSettingsActive) setOpen(true);
  }, [isSettingsActive]);

  return (
    <div>
      {/* Settings header button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          ...sb.navItem,
          width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isSettingsActive || open ? 'rgba(255,255,255,0.18)' : 'none',
          color: isSettingsActive || open ? '#fff' : 'rgba(255,255,255,0.6)',
          fontWeight: isSettingsActive || open ? 600 : 500,
          display: 'flex', alignItems: 'center', gap: 12,
          justifyContent: 'space-between',
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Settings size={18} />
          <span>Settings</span>
        </span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s', flexShrink: 0 }} />
      </button>

      {/* Sub-items */}
      {open && (
        <div style={{ paddingLeft: 14, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SETTINGS_SUB_ITEMS.map(({ to, label }) => {
            const isActive = location.pathname === to;
            return (
              <button
                key={to}
                onClick={() => navigate(to)}
                style={{
                  width: '100%', background: isActive ? 'rgba(255,255,255,0.15)' : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  padding: '8px 12px', borderRadius: 8,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all .12s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(255,255,255,0.15)' : 'none'; e.currentTarget.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.55)'; }}
              >
                <span style={{ width: 4, height: 4, borderRadius: 2, background: isActive ? '#fff' : 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Admin nav config ───────────────── */
const ADMIN_COLOR = '#1a3a5c'; // fallback only — sidebar uses school.primary_color when available
const ADMIN_NAV = [
  { to: '/admin',                Icon: Home,          labelKey: 'nav.dashboard',     end: true },
  { to: '/admin/classes',        Icon: School,        labelKey: 'nav.classes'                  },
  { to: '/admin/students',       Icon: GraduationCap, labelKey: 'nav.students'                 },
  { to: '/admin/teachers',       Icon: ClipboardCheck,labelKey: 'nav.teachers'                 },
  { to: '/admin/subjects',       Icon: BookOpen,      labelKey: 'nav.subjects'                 },
  { to: '/admin/parents',        Icon: Users,         labelKey: 'nav.parents'                  },
  { to: '/admin/fees',           Icon: Wallet,        labelKey: 'nav.fees'                     },
  { to: '/admin/payroll',        Icon: Wallet,        labelKey: 'nav.payroll'                  },
  { to: '/admin/reports',        Icon: BarChart2,     labelKey: 'nav.reports'                  },
  { to: '/admin/announcements',  Icon: Megaphone,     labelKey: 'nav.announcements'            },
  { to: '/admin/guide-center',   Icon: Compass,       labelKey: 'nav.guideCenter'              },
];

/* ─── Language Toggle ── */
function LanguageToggle() {
  const { i18n } = useTranslation();
  const isAm = i18n.language === 'am';
  return (
    <div style={{ padding: '4px 10px 8px', display: 'flex', justifyContent: 'center' }}>
      <button
        onClick={() => i18n.changeLanguage(isAm ? 'en' : 'am')}
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 20, overflow: 'hidden', cursor: 'pointer', padding: 0,
        }}
      >
        <span style={{
          padding: '4px 12px', fontSize: 11, fontWeight: 700,
          background: !isAm ? 'rgba(255,255,255,0.25)' : 'transparent',
          color: !isAm ? '#fff' : 'rgba(255,255,255,0.5)',
          transition: 'all .15s',
        }}>EN</span>
        <span style={{
          padding: '4px 12px', fontSize: 11, fontWeight: 700,
          background: isAm ? 'rgba(255,255,255,0.25)' : 'transparent',
          color: isAm ? '#fff' : 'rgba(255,255,255,0.5)',
          transition: 'all .15s',
        }}>አማ</span>
      </button>
    </div>
  );
}

/* ─── Admin Sidebar with Timetable dropdown ── */
function AdminSidebar() {
  const { user } = useAuth();
  const { profile } = useSchoolProfile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [timetableOpen, setTimetableOpen] = React.useState(false);
  const [classes, setClasses] = React.useState([]);
  const [loadingClasses, setLoadingClasses] = React.useState(false);
  const [overdueCount,    setOverdueCount]    = React.useState(0);
  const [annDraftCount,   setAnnDraftCount]   = React.useState(0);
  const dropdownRef = React.useRef(null);

  // Derive display name from school profile.
  // Empty string while loading — never show a hardcoded fallback name.
  const schoolName = profile?.name || '';
  const words = schoolName.trim().split(/\s+/);
  const brandTitle = words[0] || '';
  const brandSub = words.slice(1).join(' ') || (schoolName ? 'School' : '');

  // Fetch overdue count for Fees badge
  React.useEffect(() => {
    client.get('/fees/overview').then(({ data }) => {
      setOverdueCount(data?.data?.countOverdue || 0);
    }).catch(() => {});
  }, []);

  // Fetch draft announcements count for Announcements badge
  React.useEffect(() => {
    client.get('/admin/announcements', { params: { status: 'draft' } })
      .then(({ data }) => setAnnDraftCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
  }, []);

  // Clear draft badge when admin opens the Announcements page
  React.useEffect(() => {
    if (location.pathname === '/admin/announcements') {
      setAnnDraftCount(0);
    }
  }, [location.pathname]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const h = e => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setTimetableOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Fetch classes when dropdown opens
  React.useEffect(() => {
    if (!timetableOpen) return;
    setLoadingClasses(true);
    client.get('/admin/classes').then(({ data }) => {
      setClasses(Array.isArray(data) ? data : data.classes || []);
    }).catch(() => setClasses([])).finally(() => setLoadingClasses(false));
  }, [timetableOpen]);

  const isTimetableActive = location.pathname.includes('/timetable');
  const sidebarColor = profile?.primary_color || ADMIN_COLOR;

  return (
    <>
      <div style={{ ...sb.sidebar, background: sidebarColor }}>
        {/* Brand */}
        <div style={sb.brand}>
          {/* Show school logo if available, otherwise fallback to School icon */}
          {(profile?.logo_url || profile?.logo) ? (
            <img
              src={`${resolveUrl(profile.logo_url || profile.logo)}${(profile.logo_url || profile.logo).includes('?') ? '&' : '?'}cb=${logoCacheBust}`}
              alt="School logo"
              style={{
                width: 44, height: 44, borderRadius: 12, objectFit: 'cover',
                border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0,
              }}
            />
          ) : (
            <div style={sb.brandIcon}><School size={20} color={sidebarColor} /></div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...sb.brandTitle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{brandTitle}</div>
            <div style={sb.brandSub}>{brandSub}</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={sb.nav}>
          {ADMIN_NAV.map(({ to, Icon, labelKey, end }) => {
            const label = t(labelKey);
            const badge =
              labelKey === 'nav.fees'          ? overdueCount  :
              labelKey === 'nav.announcements' ? annDraftCount :
              0;
            return (
              <NavLink key={to} to={to} end={end}
                style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Icon size={18} />
                  {badge > 0 && (
                    <div style={{
                      position: 'absolute', top: -5, right: -6,
                      minWidth: 16, height: 16, borderRadius: 8,
                      background: '#EF4444', color: '#fff',
                      fontSize: 9, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', border: `2px solid ${sidebarColor}`,
                      lineHeight: 1,
                    }}>
                      {badge > 99 ? '99+' : badge}
                    </div>
                  )}
                </div>
                <span style={{ flex: 1 }}>{label}</span>
                {badge > 0 && (
                  <span style={{
                    background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800,
                    borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                    lineHeight: '16px', flexShrink: 0,
                  }}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* ── Settings collapsible nav ── */}
          <SettingsNavItem location={location} navigate={navigate} />

          {/* ── Timetable with class dropdown ── */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setTimetableOpen(p => !p)}
              style={{
                ...sb.navItem,
                width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: isTimetableActive || timetableOpen ? 'rgba(255,255,255,0.18)' : 'none',
                color: isTimetableActive || timetableOpen ? '#fff' : 'rgba(255,255,255,0.6)',
                fontWeight: isTimetableActive || timetableOpen ? 600 : 500,
                display: 'flex', alignItems: 'center', gap: 12,
                justifyContent: 'space-between',
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Calendar size={18} />
                <span>{t('nav.timetable')}</span>
              </span>
              <ChevronDown size={14} style={{ transform: timetableOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s', flexShrink: 0 }}/>
            </button>

            {/* Dropdown */}
            {timetableOpen && (
              <div style={{
                position: 'absolute', left: 0, right: 0, top: 'calc(100% + 2px)',
                background: 'rgba(0,0,0,0.35)', borderRadius: 10, overflow: 'hidden',
                zIndex: 50, backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.1)',
                maxHeight: 260, overflowY: 'auto',
              }}>
                {loadingClasses ? (
                  <div style={{ padding: '12px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading classes…</div>
                ) : classes.length === 0 ? (
                  <div style={{ padding: '12px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No classes found</div>
                ) : (
                  classes.map(cls => (
                    <button
                      key={cls._id}
                      onClick={() => { setTimetableOpen(false); navigate(`/admin/classes/${cls._id}/timetable`); }}
                      style={{
                        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                        padding: '9px 14px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                        color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        transition: 'background .1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800, color: '#fff',
                      }}>
                        {(cls.name || cls.grade || '?').toString().slice(0, 3).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cls.name || `Class ${cls.grade}`}</div>
                        {cls.academic_year && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>{cls.academic_year}</div>}
                      </div>
                      <ChevronRight size={12} color="rgba(255,255,255,0.4)"/>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Platform Notifications Bell */}
        <div style={{ padding: '0 10px 6px' }}>
          <AdminNotificationBell schoolId={user?.school_id} sidebarColor={sidebarColor} />
        </div>

        {/* Language toggle */}
        <LanguageToggle />

        {/* User row */}
        <div style={sb.userRow} onClick={() => setProfileOpen(true)}>
          <div style={{ ...sb.userAvatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sb.userName}>{user?.full_name || '–'}</div>
            <div style={sb.userRole}>{user?.role || '–'}</div>
          </div>
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

/* ─── Parent nav config ──────────────── */
const PARENT_NAV = [
  { to: '/parent',             Icon: Home,          label: 'Home',       end: true },
  { to: '/parent/attendance',  Icon: Calendar,      label: 'Attendance'            },
  { to: '/parent/grades',      Icon: Ribbon,        label: 'Grades'                },
  { to: '/parent/homework',    Icon: BookOpen,      label: 'Homework'              },
  { to: '/parent/timetable',   Icon: Clock,         label: 'Timetable'             },
  { to: '/parent/messages',    Icon: MessageSquare, label: 'Messages'              },
  { to: '/parent/fees',        Icon: Wallet,        label: 'Fees'                  },
  { to: '/parent/profile',     Icon: User,          label: 'Profile'               },
];

const PARENT_BOTTOM_NAV = [
  { to: '/parent',            Icon: Home,          label: 'Home',    end: true },
  { to: '/parent/homework',   Icon: BookOpen,      label: 'Homework'           },
  { to: '/parent/messages',   Icon: MessageSquare, label: 'Messages'           },
  { to: '/parent/fees',       Icon: Wallet,        label: 'Fees'               },
  { to: '/parent/profile',    Icon: User,          label: 'Profile'            },
];


/* ─── Student nav config ─────────────── */
const STUDENT_NAV = [
  { to: '/student', Icon: Home, label: 'Dashboard', end: true },
  { to: '/student/homework', Icon: BookOpen, label: 'Homework' },
  { to: '/student/grades', Icon: GraduationCap, label: 'Grades' },
  { to: '/student/attendance', Icon: Calendar, label: 'Attendance' },
  { to: '/student/timetable', Icon: Clock, label: 'Timetable' },
  { to: '/student/announcements', Icon: Bell, label: 'Announcements' },
  { to: '/student/profile', Icon: User, label: 'Profile' },
];

const STUDENT_COLOR = '#4F46E5';

/* ─── Profile Modal ──────────────────────── */
const ACCOUNTS = [
  { role: 'teacher', phone: '0922000000', password: 'teacher123', name: 'Teacher Account' },
  { role: 'parent',  phone: '0933000000', password: 'parent123',  name: 'Parent Account'  },
];
const ROLE_META = {
  teacher: { bg: '#D1FAE5', text: '#059669', label: 'Teacher' },
  parent:  { bg: '#DBEAFE', text: '#1D4ED8', label: 'Parent' },
  student: { bg: '#EEF2FF', text: '#4F46E5', label: 'Student' },
  admin:   { bg: '#F3F4F6', text: '#374151', label: 'Admin' },
};

function ProfileModal({ open, onClose }) {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = React.useState(null);
  if (!open) return null;
  const meta = ROLE_META[user?.role] || ROLE_META.teacher;
  const others = ACCOUNTS.filter(a => a.phone !== user?.phone);

  const handleSwitch = async (account) => {
    setSwitching(account.phone);
    try {
      const { data } = await client.post('/auth/login', { phone: account.phone, password: account.password });
      onClose();
      await login(data.token, data.user);
      const r = data.user.role;
      navigate(r === 'admin' ? '/admin' : r === 'teacher' ? '/teacher' : r === 'student' ? '/student' : '/parent');
    } catch (err) { console.error(err); }
    finally { setSwitching(null); }
  };

  return (
    <div style={pm.overlay} onClick={onClose}>
      <div style={pm.sheet} onClick={e => e.stopPropagation()}>
        <div style={pm.userRow}>
          <div style={{ ...pm.avatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div>
            <div style={pm.userName}>{user?.full_name}</div>
            {user?.phone && <div style={pm.userPhone}>{user.phone}</div>}
            <span style={{ ...pm.roleBadge, background: meta.bg, color: meta.text }}>{meta.label}</span>
          </div>
        </div>
        <div style={pm.divider} />
        <div style={pm.sectionTitle}>Switch Account</div>
        {others.map(acc => {
          const m = ROLE_META[acc.role] || ROLE_META.teacher;
          return (
            <button key={acc.phone} style={pm.switchBtn} onClick={() => handleSwitch(acc)} disabled={!!switching}>
              <div style={{ ...pm.switchIcon, background: m.bg, color: m.text }}>{acc.role[0].toUpperCase()}</div>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 600 }}>{acc.name}</div><div style={{ fontSize: 12, color: '#9CA3AF' }}>{acc.phone}</div></div>
              {switching === acc.phone ? <span>...</span> : <span style={{ color: '#9CA3AF' }}>›</span>}
            </button>
          );
        })}
        <div style={pm.divider} />
        <button style={pm.menuBtn} onClick={() => { onClose(); navigate('/change-password'); }}>🔒 Change Password</button>
        <button style={{ ...pm.menuBtn, color: '#DC2626' }} onClick={() => { onClose(); logout(); navigate('/login'); }}>
          ↪ Log Out / ወጣ
        </button>
      </div>
    </div>
  );
}

const pm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sheet: { background: '#fff', borderRadius: 20, padding: 24, width: 400, maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto' },
  userRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 },
  avatar: { width: 56, height: 56, borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 20, flexShrink: 0 },
  userName: { fontSize: 17, fontWeight: 700 },
  userPhone: { fontSize: 13, color: '#6B7280' },
  roleBadge: { display: 'inline-block', fontSize: 12, fontWeight: 600, borderRadius: 10, padding: '3px 10px', marginTop: 4 },
  divider: { height: 1, background: '#F3F4F6', margin: '16px 0' },
  sectionTitle: { fontSize: 13, fontWeight: 700, marginBottom: 10 },
  switchBtn: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'none', border: '1px solid #F3F4F6', borderRadius: 12, padding: '10px 12px', cursor: 'pointer', marginBottom: 6, textAlign: 'left', fontSize: 14 },
  switchIcon: { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 },
  menuBtn: { display: 'block', width: '100%', background: 'none', border: 'none', padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left' },
};

/* ─── Parent / Student Sidebar ──────────── */
function Sidebar({ items, themeColor, badges = {} }) {
  const { user } = useAuth();
  const { profile } = useSchoolProfile();
  const [profileOpen, setProfileOpen] = React.useState(false);

  const schoolName = profile?.name || '';
  const words = schoolName.trim().split(/\s+/);
  const brandTitle = words[0] || '';
  const brandSub = words.slice(1).join(' ') || (schoolName ? 'School' : '');
  const logoUrl = profile?.logo_url || profile?.logo || '';

  return (
    <>
      <div style={{ ...sb.sidebar, background: themeColor }}>
        <div style={sb.brand}>
          {logoUrl ? (
            <img
              src={`${resolveUrl(logoUrl)}${logoUrl.includes('?') ? '&' : '?'}cb=${logoCacheBust}`}
              alt="School logo"
              style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }}
            />
          ) : (
            <div style={sb.brandIcon}><School size={20} color={themeColor} /></div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...sb.brandTitle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{brandTitle}</div>
            <div style={sb.brandSub}>{brandSub}</div>
          </div>
        </div>
        <nav style={sb.nav}>
          {items.map(({ to, Icon, label, end }) => {
            const count = badges[to] || 0;
            return (
              <NavLink key={to} to={to} end={end} style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
                {({ isActive }) => (
                  <>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <Icon size={18} />
                      {count > 0 && (
                        <div style={{
                          position: 'absolute', top: -5, right: -6,
                          minWidth: 16, height: 16, borderRadius: 8,
                          background: '#EF4444', color: '#fff',
                          fontSize: 9, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 3px', border: `2px solid ${themeColor}`,
                          lineHeight: 1,
                        }}>
                          {count > 99 ? '99+' : count}
                        </div>
                      )}
                    </div>
                    <span style={{ flex: 1 }}>{label}</span>
                    {count > 0 && !isActive && (
                      <span style={{
                        fontSize: 10, fontWeight: 800,
                        background: 'rgba(239,68,68,0.2)',
                        color: '#FCA5A5',
                        borderRadius: 10, padding: '1px 6px',
                        flexShrink: 0,
                      }}>
                        {count}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div style={sb.userRow} onClick={() => setProfileOpen(true)}>
          <div style={{ ...sb.userAvatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sb.userName}>{user?.full_name || '–'}</div>
            <div style={sb.userRole}>{user?.role || '–'}</div>
          </div>
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

const sb = {
  sidebar: { width: 220, display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100vh' },
  brand: { display: 'flex', alignItems: 'center', gap: 10, padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 8 },
  brandIcon: { width: 38, height: 38, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  brandTitle: { fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: 0.5 },
  brandSub: { fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: 1.5, textTransform: 'uppercase' },
  nav: { flex: 1, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all .12s' },
  navActive: { background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600 },
  userRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px 18px', margin: '0 10px', borderRadius: 12, background: 'rgba(0,0,0,0.15)', cursor: 'pointer' },
  userAvatar: { width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userName: { fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { fontSize: 10, color: 'rgba(255,255,255,0.65)', textTransform: 'capitalize', marginTop: 1 },
};

/* ─── Mobile detection ───────────────────── */
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = React.useState(() => window.innerWidth < bp);
  React.useEffect(() => {
    const h = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return mobile;
}

/* ─── Student bottom nav (mobile only) ───── */
const STUDENT_PRIMARY_NAV = [
  { to: '/student',            Icon: Home,          label: 'Home',      end: true },
  { to: '/student/homework',   Icon: BookOpen,      label: 'Homework'             },
  { to: '/student/grades',     Icon: GraduationCap, label: 'Grades'               },
  { to: '/student/attendance', Icon: Calendar,      label: 'Attendance'           },
];

const STUDENT_MORE_NAV = [
  { to: '/student/timetable',     Icon: Clock, label: 'Timetable'     },
  { to: '/student/announcements', Icon: Bell,  label: 'Announcements' },
  { to: '/student/profile',       Icon: User,  label: 'Profile'       },
];

function StudentBottomNav({ annCount = 0 }) {
  const { color } = useSchoolBrand();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const moreActive = STUDENT_MORE_NAV.some(({ to }) => location.pathname.startsWith(to));

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div
          onClick={() => setMoreOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.35)' }}
        />
      )}

      {/* More sheet — slides up just above the nav bar */}
      <div style={{
        position: 'fixed', left: 0, right: 0, zIndex: 199,
        bottom: moreOpen ? 64 : -160,
        background: '#fff',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
        padding: '16px 12px 8px',
        transition: 'bottom 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', gap: 10,
      }}>
        {STUDENT_MORE_NAV.map(({ to, Icon, label }) => {
          const isActive = location.pathname.startsWith(to);
          const isAnn = label === 'Announcements';
          return (
            <button key={to}
              onClick={() => { navigate(to); setMoreOpen(false); }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 8, padding: '14px 8px',
                background: isActive ? `${color}15` : '#F9FAFB',
                border: `1.5px solid ${isActive ? `${color}50` : '#E5E7EB'}`,
                borderRadius: 14, cursor: 'pointer',
                color: isActive ? color : '#6B7280',
              }}>
              <div style={{ position: 'relative' }}>
                <Icon size={22} strokeWidth={isActive ? 2.5 : 1.8} />
                {isAnn && annCount > 0 && (
                  <div style={{
                    position: 'absolute', top: -4, right: -6,
                    minWidth: 15, height: 15, borderRadius: 8,
                    background: '#EF4444', color: '#fff',
                    fontSize: 8, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px', border: '1.5px solid #fff',
                  }}>
                    {annCount > 99 ? '99+' : annCount}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500 }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Bottom nav bar */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
        background: '#fff', borderTop: '1px solid #E5E7EB',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        display: 'flex',
      }}>
        {STUDENT_PRIMARY_NAV.map(({ to, Icon, label, end }) => (
          <NavLink key={to} to={to} end={end}
            style={({ isActive }) => ({
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 3, padding: '8px 4px',
              textDecoration: 'none',
              color: isActive ? color : '#9CA3AF',
              borderTop: isActive ? `2px solid ${color}` : '2px solid transparent',
            })}>
            {({ isActive }) => (
              <>
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2 }}>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        {/* More button */}
        <button
          onClick={() => setMoreOpen(o => !o)}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3, padding: '8px 4px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderTop: (moreActive || moreOpen) ? `2px solid ${color}` : '2px solid transparent',
            color: (moreActive || moreOpen) ? color : '#9CA3AF',
          }}>
          <div style={{ position: 'relative' }}>
            <MoreHorizontal size={20} strokeWidth={(moreActive || moreOpen) ? 2.5 : 1.8} />
            {annCount > 0 && !moreOpen && (
              <div style={{
                position: 'absolute', top: -4, right: -6,
                minWidth: 15, height: 15, borderRadius: 8,
                background: '#EF4444', color: '#fff',
                fontSize: 8, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px', border: '1.5px solid #fff',
              }}>
                {annCount > 99 ? '99+' : annCount}
              </div>
            )}
          </div>
          <span style={{ fontSize: 10, fontWeight: (moreActive || moreOpen) ? 700 : 500, letterSpacing: 0.2 }}>More</span>
        </button>
      </nav>
    </>
  );
}

/* ─── Layouts ────────────────────────────── */
function AdminLayout({ children }) {
  const { user } = useAuth();
  const isImpersonating = !!(() => { try { return JSON.parse(localStorage.getItem('impersonating') || 'null'); } catch { return null; } })();
  const schoolId = user?.school_id;
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f5f5f0' }}>
      <ImpersonationBanner />
      <AdminSidebar />
      <main style={{ flex: 1, overflow: 'auto', marginTop: isImpersonating ? 44 : 0 }}>{children}</main>
    </div>
  );
}

function ParentBottomNav({ annCount = 0 }) {
  const { color } = useSchoolBrand();
  return (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #E5E7EB', display: 'flex', zIndex: 200, boxShadow: '0 -2px 12px rgba(0,0,0,0.08)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {PARENT_BOTTOM_NAV.map(({ to, Icon, label, end }) => {
        const showDot = label === 'Home' && annCount > 0;
        return (
          <NavLink key={to} to={to} end={end}
            style={({ isActive }) => ({ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '8px 4px', textDecoration: 'none', color: isActive ? color : '#9CA3AF', borderTop: isActive ? `2px solid ${color}` : '2px solid transparent' })}>
            {({ isActive }) => (
              <>
                <div style={{ position: 'relative' }}>
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                  {showDot && (
                    <div style={{ position: 'absolute', top: -2, right: -4, width: 8, height: 8, borderRadius: 4, background: '#EF4444', border: '1.5px solid #fff' }} />
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2 }}>{label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function ParentLayoutInner({ children }) {
  const { color } = useSchoolBrand();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [annCount, setAnnCount] = React.useState(0);
  const schoolId = user?.school_id;

  React.useEffect(() => {
    client.get('/parent/announcements')
      .then(({ data }) => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = (data || []).filter(a => new Date(a.createdAt).getTime() > cutoff);
        setAnnCount(recent.length);
      })
      .catch(() => {});
  }, []);

  // Clear badge when parent visits Home (where announcements are displayed)
  React.useEffect(() => {
    if (location.pathname === '/parent') {
      setAnnCount(0);
    }
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', height: '100vh', background: COLORS.bg }}>
      {!isMobile && <Sidebar items={PARENT_NAV} themeColor={color} badges={{ '/parent': annCount }} />}
      <main style={{ flex: 1, overflow: 'auto', paddingBottom: isMobile ? 64 : 0 }}>{children}</main>
      {isMobile && <ParentBottomNav annCount={annCount} />}
    </div>
  );
}

function ParentLayout({ children }) {
  return (
    <SchoolBrandProvider>
      <ParentLayoutInner>{children}</ParentLayoutInner>
    </SchoolBrandProvider>
  );
}

function StudentLayoutInner({ children }) {
  const { color } = useSchoolBrand();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [annCount, setAnnCount] = React.useState(0);
  const schoolId = user?.school_id;

  React.useEffect(() => {
    client.get('/student/announcements')
      .then(({ data }) => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = (data || []).filter(a => a.source === 'announcement' && new Date(a.sent_at || a.createdAt).getTime() > cutoff);
        setAnnCount(recent.length);
      })
      .catch(() => {});
  }, []);

  // Clear badge when student opens the Announcements page
  React.useEffect(() => {
    if (location.pathname === '/student/announcements') {
      setAnnCount(0);
    }
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', height: '100vh', background: COLORS.bg }}>
      {!isMobile && <Sidebar items={STUDENT_NAV} themeColor={color} badges={{ '/student/announcements': annCount }} />}
      <main style={{ flex: 1, overflow: 'auto', paddingBottom: isMobile ? 64 : 0, display: 'flex', flexDirection: 'column' }}>
        {children}
      </main>
      {isMobile && <StudentBottomNav annCount={annCount} />}
    </div>
  );
}

function StudentLayout({ children }) {
  return (
    <SchoolBrandProvider>
      <StudentLayoutInner>{children}</StudentLayoutInner>
    </SchoolBrandProvider>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  if (!token || !user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/login" replace />;

  // If user must change password, redirect to change-password page (unless already there or impersonating)
  if (user.must_change_password && !user.impersonated_by && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Terms gate — show modal if user hasn't accepted the current version
  // Skip for superadmin and SA-impersonated sessions
  const isImpersonatedSession = user.impersonated_by === 'superadmin';
  const needsTerms = user.role !== 'superadmin' && !isImpersonatedSession &&
    (!user.terms_accepted_version || user.terms_accepted_version !== CURRENT_TERMS_VERSION);

  if (needsTerms) {
    return (
      <>
        {/* Render the page behind the modal so layout is ready */}
        <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}>
          {children}
        </div>
        <TermsModal
          onAccepted={() => {
            // Update local user state so the modal disappears without re-login
            updateUser({ ...user, terms_accepted_version: CURRENT_TERMS_VERSION });
          }}
          onLogout={() => {
            logout();
            navigate('/login', { replace: true });
          }}
        />
      </>
    );
  }

  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'superadmin') return <Navigate to="/sa"      replace />;
  if (user.role === 'admin')      return <Navigate to="/admin"   replace />;
  if (user.role === 'teacher')    return <Navigate to="/teacher" replace />;
  if (user.role === 'student')    return <Navigate to="/student" replace />;
  if (user.role === 'parent')     return <Navigate to="/parent"  replace />;
  return <Navigate to="/login" replace />;
}

// Root "/" — authenticated users go to their portal; unauthenticated see the public homepage.
function PublicRootRoute() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  if (token && user) return <RoleRedirect />;
  return <PublicLayout><PublicHome /></PublicLayout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public marketing routes ── */}
            <Route path="/"              element={<PublicRootRoute />} />
            <Route path="/features"      element={<PublicLayout><PublicFeatures    /></PublicLayout>} />
            <Route path="/pricing"       element={<PublicLayout><PublicPricing     /></PublicLayout>} />
            <Route path="/about"         element={<PublicLayout><PublicAbout       /></PublicLayout>} />
            <Route path="/contact"       element={<PublicLayout><PublicContact     /></PublicLayout>} />
            <Route path="/request-demo"  element={<PublicLayout><PublicRequestDemo /></PublicLayout>} />
            <Route path="/privacy-policy" element={<PublicLayout><PublicPrivacyPolicy /></PublicLayout>} />

            {/* ── Auth routes ── */}
            <Route path="/login" element={<LoginGateway />} />
            <Route path="/login/staff" element={<Login />} />
            <Route path="/parent-login" element={<ParentLogin />} />
            <Route path="/student/login" element={<StudentLogin />} />

            {/* Admin routes */}
            <Route path="/admin"                    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminDashboard  /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminClasses    /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes/:classId"              element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminClassDetail/></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes/:classId/timetable"    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><TimetableBuilder/></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/students"           element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminStudents   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/teachers"           element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminTeachers   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/subjects"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminSubjects   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents"                element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminParents      /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents/:id"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminParentDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents/:parentId/slip" element={<ProtectedRoute allowedRoles={['admin']}><AdminParentSlip /></ProtectedRoute>} />
            <Route path="/admin/fees"               element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminFees       /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/fees/types"         element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminFeeTypes   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/fees/gateway"       element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><GatewaySettings /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/reports"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminReports       /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/announcements"     element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminAnnouncements /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/guide-center"      element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminGuideCenter   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/payroll"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminPayroll       /></AdminLayout></ProtectedRoute>} />

            {/* Settings routes */}
            <Route path="/admin/settings/school-profile" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><SettingsSchoolProfile /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/academic-years" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><SettingsAcademicYears /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/terms"          element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><SettingsTerms         /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/users-roles"    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><SettingsUsersRoles    /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/payroll"        element={<Navigate to="/admin/payroll" replace />} />

            {/* Public payment return page — no auth required */}
            <Route path="/pay/return" element={<PayReturn />} />

            {/* Teacher routes */}
            <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherHome /></ProtectedRoute>} />
            <Route path="/teacher/attendance" element={<ProtectedRoute allowedRoles={['teacher','admin']}><AttendanceSelectClass /></ProtectedRoute>} />
            <Route path="/teacher/attendance/:classId" element={<ProtectedRoute allowedRoles={['teacher','admin']}><AttendanceMarking /></ProtectedRoute>} />
            <Route path="/teacher/grades" element={<ProtectedRoute allowedRoles={['teacher']}><GradesSelectClass /></ProtectedRoute>} />
            <Route path="/teacher/grades/:classId" element={<ProtectedRoute allowedRoles={['teacher']}><GradesTable /></ProtectedRoute>} />
            <Route path="/teacher/alerts" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherAlerts /></ProtectedRoute>} />
            <Route path="/teacher/profile" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherProfile /></ProtectedRoute>} />
            <Route path="/teacher/notes" element={<ProtectedRoute allowedRoles={['teacher']}><ClassNotes /></ProtectedRoute>} />
            <Route path="/teacher/students" element={<ProtectedRoute allowedRoles={['teacher']}><ViewStudents /></ProtectedRoute>} />
            <Route path="/teacher/students/:studentId" element={<ProtectedRoute allowedRoles={['teacher']}><StudentDetail /></ProtectedRoute>} />
            <Route path="/teacher/report" element={<ProtectedRoute allowedRoles={['teacher']}><ClassReport /></ProtectedRoute>} />
            <Route path="/teacher/timetable" element={<ProtectedRoute allowedRoles={['teacher']}><MyTimetable /></ProtectedRoute>} />
            <Route path="/teacher/messages" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherMessages /></ProtectedRoute>} />
            <Route path="/teacher/homework" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherHomework /></ProtectedRoute>} />
            <Route path="/teacher/homework/create" element={<ProtectedRoute allowedRoles={['teacher']}><CreateHomework /></ProtectedRoute>} />
            <Route path="/teacher/homework/:homeworkId/submissions" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherSubmissions /></ProtectedRoute>} />
            <Route path="/teacher/classes/:classId" element={<ProtectedRoute allowedRoles={['teacher']}><ClassDashboard /></ProtectedRoute>} />
            <Route path="/teacher/notifications" element={<ProtectedRoute allowedRoles={['teacher']}><NotificationCenter /></ProtectedRoute>} />
            <Route path="/teacher/payslip"       element={<ProtectedRoute allowedRoles={['teacher']}><TeacherPayslip    /></ProtectedRoute>} />

            {/* Parent routes */}
            <Route path="/parent"              element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentHome /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/attendance"   element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentAttendance /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/grades"       element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentGrades /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/fees"         element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentFees /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/homework"     element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentHomework /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/messages"     element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentMessages /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/timetable"    element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentTimetable /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/profile"      element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentProfile /></ParentLayout></ProtectedRoute>} />

            {/* Student routes */}
            <Route path="/student" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentHome /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/homework" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentHomework /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/grades" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentGrades /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/attendance" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentAttendance /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/timetable" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentTimetable /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/announcements" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentAnnouncements /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/profile" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentProfile /></StudentLayout></ProtectedRoute>} />

            {/* Admin — Student Slip (printable, no sidebar) */}
            <Route path="/admin/students/:studentId/slip" element={<ProtectedRoute allowedRoles={['admin']}><AdminStudentSlip /></ProtectedRoute>} />

            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/change-pin"      element={<ProtectedRoute allowedRoles={['parent','student']}><ChangePIN /></ProtectedRoute>} />

            {/* Super Admin Portal — no auth wrapper, handles its own auth */}
            <Route path="/sa/*" element={<SuperAdminPortal />} />

            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
